import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import Breadcrumbs from '../Components/Breadcrumbs';
import PageLoader from '../Components/PageLoader';
import Button from '../Components/Buttons';
import { useAlert } from '../Components/AlertContext';
import { TaskScheduleApiProvider, useTaskScheduleApi } from '../Services/TaskScheduleApiProvider';
import type { TaskScheduleExecution as TaskScheduleExecutionType } from '../Services/TaskScheduleApiProviderTypes';
import { formatDateUTC } from '../utils/formatDate';

const STATUS_MAP: Record<string, { class: string; label: string }> = {
  pending: { class: 'pending', label: 'Pending' },
  done: { class: 'active', label: 'Done' },
  rejected: { class: 'inactive', label: 'Rejected' },
};

interface DetailItemProps {
  label: string;
  value: string;
}

function DetailItem({ label, value }: DetailItemProps) {
  return (
    <div className="detail-item">
      <label>{label}</label>
      <span>{value}</span>
    </div>
  );
}

function TaskScheduleExecutionContent() {
  const navigate = useNavigate();
  const { showAlert } = useAlert();
  const { taskScheduleId, executionId } = useParams();
  const { getTaskScheduleExecution } = useTaskScheduleApi();

  const [loading, setLoading] = useState(true);
  const [scheduleExecution, setScheduleExecution] = useState<TaskScheduleExecutionType | null>(
    null,
  );

  const handleBack = useCallback(() => navigate(-1), [navigate]);

  const fetchExecution = useCallback(async () => {
    if (!executionId || !taskScheduleId) return;

    setLoading(true);

    try {
      const execution = await getTaskScheduleExecution(taskScheduleId, executionId);
      setScheduleExecution(execution);
    } catch {
      showAlert('fail', 'Failed to fetch task schedule execution');
    } finally {
      setLoading(false);
    }
  }, [executionId, taskScheduleId, getTaskScheduleExecution, showAlert]);

  useEffect(() => {
    fetchExecution();
  }, [fetchExecution]);

  if (loading) return <PageLoader />;
  if (!scheduleExecution) return <div>No execution found</div>;

  const status = STATUS_MAP[scheduleExecution.status] ?? {
    class: 'unknown',
    label: scheduleExecution.status,
  };

  const details: DetailItemProps[] = [
    { label: 'Assistant Key', value: scheduleExecution.assistant_key },
    { label: 'Task Schedule Key', value: scheduleExecution.task_schedule_key },
    { label: 'Created At', value: formatDateUTC(scheduleExecution.created_at) },
    { label: 'Updated At', value: formatDateUTC(scheduleExecution.updated_at) },
    {
      label: 'Finished At',
      value: scheduleExecution.finished_at ? formatDateUTC(scheduleExecution.finished_at) : '-',
    },
  ];

  return (
    <div className="container">
      <Breadcrumbs />

      <div className="radius-yes p-2 bg-yes col-12 h-100">
        <div className="org-header">
          <h2>{scheduleExecution.id}</h2>
          <span className={`status-badge ${status.class}`}>{status.label}</span>
        </div>

        {details.map(({ label, value }) => (
          <DetailItem key={label} label={label} value={value} />
        ))}

        <div className="buttons-container d-flex justify-end">
          <Button variant="primary" size="medium" onClick={handleBack}>
            Back
          </Button>
        </div>
      </div>
    </div>
  );
}

function TaskScheduleExecution() {
  return (
    <TaskScheduleApiProvider>
      <TaskScheduleExecutionContent />
    </TaskScheduleApiProvider>
  );
}

export default TaskScheduleExecution;
